/**
 * Receipt resolution for the Drafts review/approve/unshare actions.
 *
 * Regression this exists to kill: the action buttons used the pattern
 * `onReview={() => todayDaily && setEditing(todayDaily)}` — when the card's
 * STATUS (from `receipts.weekStatus`) said "reviewable" but the matching ROW
 * was missing from the `receipts.forWeek` cache (refetch race right after
 * capture→draft, or a stale cache when arriving from Home's deep link), the
 * click was a SILENT NO-OP: a button that looks tappable and does nothing.
 *
 * `resolveReceiptFor` makes the open path deterministic:
 *   1. use the click-time snapshot if the caller has one;
 *   2. else look in the cached week payload;
 *   3. else fetch a FRESH week payload (injected — the tRPC utils fetch)
 *      and look again;
 *   4. still nothing → a typed error the caller MUST surface (toast),
 *      never silence.
 */

type ReceiptLike = { periodKey: string; [k: string]: unknown };

export type WeekPayload = {
  weekly?: ReceiptLike | null;
  daily: ReceiptLike[];
} | null | undefined;

/** Pure lookup. `periodKey` is ignored for weeklies when undefined — the
 *  payload's weekly already belongs to the viewed week. */
export function findReceiptIn(
  data: WeekPayload,
  kind: "daily" | "weekly",
  periodKey?: string,
): ReceiptLike | null {
  if (!data) return null;
  if (kind === "weekly") {
    const w = data.weekly ?? null;
    if (!w) return null;
    return periodKey == null || w.periodKey === periodKey ? w : null;
  }
  if (!periodKey) return null;
  return data.daily?.find(r => r.periodKey === periodKey) ?? null;
}

export type ResolveResult =
  | { ok: true; receipt: ReceiptLike }
  | { ok: false; reason: "not_found" | "fetch_failed" };

export async function resolveReceiptFor(args: {
  kind: "daily" | "weekly";
  periodKey?: string;
  /** Click-time row, when the caller already holds one. */
  snapshot?: ReceiptLike | null;
  /** The current (possibly stale) week payload from the query cache. */
  cached: WeekPayload;
  /** Fresh fetch — e.g. `() => utils.receipts.forWeek.fetch(input)`. */
  fetchFresh: () => Promise<WeekPayload>;
}): Promise<ResolveResult> {
  const local =
    args.snapshot ?? findReceiptIn(args.cached, args.kind, args.periodKey);
  if (local) return { ok: true, receipt: local };
  let fresh: WeekPayload;
  try {
    fresh = await args.fetchFresh();
  } catch {
    return { ok: false, reason: "fetch_failed" };
  }
  const found = findReceiptIn(fresh, args.kind, args.periodKey);
  return found ? { ok: true, receipt: found } : { ok: false, reason: "not_found" };
}
